import { Grid, GridItem } from '@rsa-digital/evo-shared-components/components/Grid';
import {
  InPageNavDesktop,
  InPageNavMobile,
} from '@rsa-digital/evo-shared-components/components/InPageNav';
import RichText from '@rsa-digital/evo-shared-components/components/RichText';
import { graphql } from 'gatsby';
import kebabCase from 'lodash/kebabCase';
import React, { Fragment, ReactElement } from 'react';
import { warningWithDetail } from 'helpers/errorReporting';
import ExpandableRelatedContentCards from '../ExpandableRelatedContentCards';
import { ExpandableRelatedContentCardsData } from '../types';

export type ExpandableRelatedContentCardsWithInAppNavProps = {
  in_page_navigation_title: string;
  sections: InPageNavRelatedContentCardProps[];
};

export type InPageNavRelatedContentCardProps = {
  page_section: {
    page_blocks: CsPageBlock[];
  };
  nav_section: {
    inline_nav_text: string;
  };
};

type CsPageBlock = {
  page_text: {
    text: string;
  } | null;
  related_content_cards: ExpandableRelatedContentCardsData;
};

export const query = graphql`
  fragment ExpandableRelatedContentCardsWithInPageNav on cs__related_content_cards_with_in_page_navigation {
    in_page_navigation_title
    sections {
      page_section {
        page_blocks {
          page_text {
            text
          }
          related_content_cards {
            show_more_cta {
              ...Cta
            }
            show_less_cta {
              ...Cta
            }
            content_cards {
              ...RelatedContentCardBlock
            }
          }
        }
      }
      nav_section {
        inline_nav_text
      }
    }
  }
`;

const processBlock = (id: string | undefined, block: CsPageBlock): ReactElement => {
  if (block.page_text) {
    return <RichText id={id} html={block.page_text?.text ?? ''} focusable />;
  }

  if (block.related_content_cards) {
    return (
      <ExpandableRelatedContentCards
        id={id}
        key={id}
        expandable_related_content_cards={{
          content_cards: block.related_content_cards.content_cards,
          show_more_cta: block.related_content_cards.show_more_cta,
          show_less_cta: block.related_content_cards.show_less_cta,
        }}
      />
    );
  }
  warningWithDetail(
    'Unrecognised block.',
    'Have you added a new block type to in page nav related content cards?'
  );
  return <></>;
};

const ExpandableRelatedContentCardsWithInPageNav: React.FC<
  ExpandableRelatedContentCardsWithInAppNavProps
> = ({ in_page_navigation_title, sections }) => {
  const navSections = sections
    .filter((section) => !!section.nav_section)
    .map((section) => ({
      text: section.nav_section.inline_nav_text,
      id: kebabCase(section.nav_section.inline_nav_text),
    }));

  const pageSections = sections
    .filter((section) => section.page_section != null)
    .map((section) =>
      section.page_section.page_blocks.map((block) => {
        const sectionIndex = sections.indexOf(section);
        const previousSection = sections[sectionIndex - 1];
        const previousNavSectionText = previousSection?.nav_section?.inline_nav_text;

        const id = previousNavSectionText ? kebabCase(previousNavSectionText) : undefined;
        return <Fragment key={id}>{processBlock(id, block)}</Fragment>;
      })
    );
  return (
    <>
      <Grid alignLeft>
        <GridItem desktop={1} tabletLandscape={1} />
        <GridItem desktop={8} tabletLandscape={8}>
          {navSections.length > 0 && (
            <InPageNavMobile
              navHeading={in_page_navigation_title}
              navSections={navSections}
              data-cy="Mobile in-page nav"
            />
          )}
          <div>{pageSections}</div>
        </GridItem>
        {navSections.length > 0 && (
          <GridItem desktop={3} tabletLandscape={3}>
            <InPageNavDesktop
              navHeading={in_page_navigation_title}
              navSections={navSections}
              data-cy="Desktop in-page nav"
            />
          </GridItem>
        )}
      </Grid>
    </>
  );
};

export default React.memo(ExpandableRelatedContentCardsWithInPageNav);
